import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, RadixDialog, RadixDialogContent, RadixDialogTitle, RadixDialogDescription } from '@crabs-ai/ds-react';
import { staticFile } from 'remotion';
import { PlayerRefContext } from '../context-provider';
import { getLocalUrls } from '../caching/load-to-blob-url';
import { getPreparedNativeAssetPath } from '@waka/render/native-prepared-assets';
import { detectSilenceSegments } from '../utils/silence-detection';
import { useCurrentStateAsRef, useFullState, useWriteContext } from '../utils/use-context';
import { Z_INDEX_EDITOR_DIALOG_OVERLAY } from '../z-indices';
import { RemoveSilenceDialog, type RemoveSilenceDialogConfirmParams } from './remove-silence-dialog';
import { applySilenceReview, detectSilenceReview, estimatedSilenceSeconds, isSilenceReviewCurrent, silencePreviewWindow, silenceReviewTargets, type SilenceCandidate, type SilenceReviewSession } from './silence-review';

export function SilenceReviewDialog({ open, itemIds, onOpenChange }: {
  open: boolean; itemIds: string[]; onOpenChange: (open: boolean) => void;
}) {
  const state = useFullState();
  const stateRef = useCurrentStateAsRef();
  const { setState } = useWriteContext();
  const playerRef = useContext(PlayerRefContext);
  const [session, setSession] = useState<SilenceReviewSession | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [stale, setStale] = useState(false);
  const [message, setMessage] = useState('');
  const [playing, setPlaying] = useState<string | null>(null);
  const generation = useRef(0);
  const activeSnapshot = useRef<Pick<SilenceReviewSession, 'before' | 'linking' | 'selection'> | null>(null);
  const preview = useRef<HTMLAudioElement | null>(null);
  const previewTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPreview = useCallback(() => {
    if (previewTimer.current !== null) clearInterval(previewTimer.current);
    previewTimer.current = null;
    if (preview.current) {
      preview.current.pause();
      preview.current.onloadedmetadata = null;
      preview.current.onerror = null;
      preview.current.onended = null;
      preview.current.removeAttribute('src');
      preview.current.load();
      preview.current = null;
    }
    setPlaying(null);
  }, []);

  useEffect(() => {
    generation.current++;
    activeSnapshot.current = null;
    setSession(null); setSelected(new Set()); setBusy(false); setStale(false); setMessage('');
    if (open) playerRef?.current?.pause();
    return () => { generation.current++; activeSnapshot.current = null; stopPreview(); };
  }, [open, itemIds, playerRef, stopPreview]);

  // Invalidation is sticky, including a transient drag that is later cancelled.
  useEffect(() => {
    if (!open || !activeSnapshot.current || isSilenceReviewCurrent(state, activeSnapshot.current)) return;
    generation.current++;
    activeSnapshot.current = null;
    setStale(true); setBusy(false); stopPreview();
    setMessage('편집 내용이나 연결 설정이 바뀌었습니다. 현재 클립에서 다시 검출해 주세요.');
  }, [open, state, stopPreview]);

  const changeOpen = useCallback((next: boolean) => {
    if (!next) { generation.current++; activeSnapshot.current = null; stopPreview(); }
    onOpenChange(next);
  }, [onOpenChange, stopPreview]);

  const handleDetect = useCallback(async (params: RemoveSilenceDialogConfirmParams) => {
    stopPreview();
    const before = stateRef.current;
    const token = ++generation.current;
    const snapshot = { before: before.undoableState, linking: before.isLinkingEnabled, selection: [...before.selectedItems] };
    const targets = silenceReviewTargets(before, itemIds, (assetId) => {
      const asset = before.undoableState.assets[assetId];
      if (!asset) return null;
      const prepared = getPreparedNativeAssetPath(asset);
      return prepared ? staticFile(prepared) : (getLocalUrls()[assetId] ?? asset.remoteUrl ?? (asset.staticPath ? staticFile(asset.staticPath) : null));
    });
    if (!targets.length) {
      setMessage('검출할 수 있는 클립이 없습니다. 배경음악은 제외됩니다. 잠금과 오디오 소스를 확인한 뒤 클립을 다시 선택해 주세요.');
      return;
    }
    activeSnapshot.current = snapshot;
    setBusy(true); setStale(false); setMessage('무음 후보를 검출하고 있습니다. 아직 편집에는 적용되지 않습니다.');
    try {
      const result = await detectSilenceReview(before, targets, params, async (input) => {
        if (generation.current !== token || !isSilenceReviewCurrent(stateRef.current, snapshot)) throw new Error('silence-review-cancelled');
        return detectSilenceSegments(input);
      });
      if (generation.current !== token) return;
      if (!isSilenceReviewCurrent(stateRef.current, result)) {
        setStale(true); setMessage('편집 내용이 바뀌었습니다. 다시 검출해 주세요.'); return;
      }
      setSession(result); setSelected(new Set(result.candidates.map((candidate) => candidate.id))); setMessage('');
    } catch (error) {
      if (generation.current !== token) return;
      activeSnapshot.current = null;
      setMessage(error instanceof Error && error.message !== 'silence-review-cancelled'
        ? `무음 검출에 실패했습니다: ${error.message}. 다시 검출할 수 있습니다.`
        : '편집 내용이 바뀌었습니다. 다시 검출해 주세요.');
    } finally { if (generation.current === token) setBusy(false); }
  }, [itemIds, stateRef, stopPreview]);

  const handlePreview = useCallback((candidate: SilenceCandidate) => {
    const wasPlaying = playing === candidate.id;
    stopPreview();
    if (wasPlaying || !session || stale || !isSilenceReviewCurrent(stateRef.current, session)) return;
    playerRef?.current?.pause();
    const audio = new Audio();
    preview.current = audio;
    audio.preload = 'auto'; audio.playbackRate = candidate.target.playbackRate;
    const window = silencePreviewWindow(candidate);
    setPlaying(candidate.id);
    const fail = () => {
      if (preview.current !== audio) return;
      stopPreview(); setMessage('문맥 재생에 실패했습니다. 소스를 확인하고 다시 미리듣기를 눌러 주세요.');
    };
    audio.onerror = fail;
    audio.onended = () => { if (preview.current === audio) stopPreview(); };
    audio.onloadedmetadata = () => {
      if (preview.current !== audio) return;
      audio.currentTime = window.start;
      previewTimer.current = setInterval(() => {
        if (audio.currentTime >= window.end) stopPreview();
      }, 20);
    };
    audio.src = candidate.target.audioUrl;
    audio.load();
    audio.currentTime = window.start;
    // Start inside the click gesture; metadata may arrive after browser user activation expires.
    void audio.play().catch(fail);
  }, [playing, session, stale, stateRef, playerRef, stopPreview]);

  const handleApply = useCallback(() => {
    stopPreview();
    if (!session || stale || !selected.size) return;
    const before = stateRef.current;
    if (!isSilenceReviewCurrent(before, session)) {
      setStale(true); setMessage('편집 내용이 바뀌었습니다. 다시 검출해 주세요.'); return;
    }
    // Materialize random split IDs before React can replay the updater.
    const after = applySilenceReview(before, session, selected);
    if (after === before) { setMessage('적용할 변경이 없습니다. 선택 구간과 트랙 잠금을 확인해 주세요.'); return; }
    setState({ expectedUndoableState: session.before, commitToUndoStack: true,
      update: (current) => isSilenceReviewCurrent(current, session) ? { ...current, undoableState: after.undoableState,
        selectedItems: after.selectedItems, timelineTimeEditApplied: true } : current });
    changeOpen(false);
  }, [session, stale, selected, stateRef, setState, stopPreview, changeOpen]);

  const retry = useCallback(() => {
    generation.current++; activeSnapshot.current = null; stopPreview();
    setSession(null); setStale(false); setBusy(false); setMessage('설정을 확인하고 현재 클립에서 다시 검출하세요.');
  }, [stopPreview]);

  if (!open) return null;
  if (!session) return <RemoveSilenceDialog open={open} onOpenChange={changeOpen} onConfirm={handleDetect}
    closeOnConfirm={false} busy={busy} confirmLabel={stale ? '다시 검출' : '무음 검출'} statusMessage={message} />;
  const seconds = (value: number) => `${value.toFixed(2)}초`;
  return <RadixDialog open={open} onOpenChange={changeOpen}>
    <RadixDialogContent className="max-w-[620px] max-h-[90vh] overflow-y-auto" style={{ zIndex: Z_INDEX_EDITOR_DIALOG_OVERLAY }}
      overlayProps={{ style: { zIndex: Z_INDEX_EDITOR_DIALOG_OVERLAY } }} onKeyDown={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}>
      <RadixDialogTitle>무음 구간 검토</RadixDialogTitle>
      <RadixDialogDescription>제거할 구간만 선택하세요. 유지할 쉼은 선택을 해제하고, 앞뒤 문맥을 들어볼 수 있습니다.</RadixDialogDescription>
      <p className="text-body-small">연결 {session.linking ? '켜짐 · 연결된 메인 트랙의 같은 시간을 함께 줄입니다.' : '꺼짐 · 각 대상 트랙의 시간을 줄입니다.'} 잠긴 트랙과 배경음악은 유지됩니다. 선택하지 않은 별도 오디오 트랙은 이동하지 않습니다.</p>
      <p role="status" aria-live="polite">{stale ? message : `${session.candidates.length}개 후보 · ${selected.size}개 선택 · 선택 트랙 예상 단축 ${seconds(estimatedSilenceSeconds(session, selected))}`}</p>
      {!stale && message && <p role="alert">{message}</p>}
      <div className="flex gap-2xs">
        <Button hierarchy="tertiary" size="small" disabled={stale} onClick={() => setSelected(new Set(session.candidates.map((candidate) => candidate.id)))}>모두 선택</Button>
        <Button hierarchy="tertiary" size="small" disabled={stale} onClick={() => setSelected(new Set())}>선택 없음</Button>
      </div>
      {!session.candidates.length && <p>검출된 무음이 없습니다. 설정을 바꿔 다시 검출할 수 있습니다.</p>}
      <ul aria-label="검출된 무음 구간" className="grid gap-2xs max-h-[40vh] overflow-y-auto">
        {session.candidates.map((candidate, index) => <li key={candidate.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-sm">
          <label className="flex min-w-0 items-center gap-2xs">
            <input type="checkbox" disabled={stale} checked={selected.has(candidate.id)} onChange={(event) => {
              const checked = event.target.checked;
              setSelected((previous) => { const next = new Set(previous); if (checked) next.add(candidate.id); else next.delete(candidate.id); return next; });
            }} />
            <span className="break-words">{index + 1}. {candidate.target.label} · 클립 {seconds(candidate.segment.startInSeconds)}–{seconds(candidate.segment.endInSeconds)} · {seconds(candidate.segment.endInSeconds - candidate.segment.startInSeconds)}</span>
          </label>
          <Button hierarchy="tertiary" size="small" disabled={stale} aria-label={`${index + 1}번 구간 문맥 ${playing === candidate.id ? '정지' : '미리듣기'}`} onClick={() => handlePreview(candidate)}>{playing === candidate.id ? '정지' : '미리듣기'}</Button>
        </li>)}
      </ul>
      <div className="flex justify-end gap-2xs">
        <Button hierarchy="tertiary" size="medium" onClick={retry}>다시 검출</Button>
        <Button hierarchy="tertiary" size="medium" onClick={() => changeOpen(false)}>취소</Button>
        <Button hierarchy="primary" size="medium" disabled={stale || !selected.size} onClick={handleApply}>선택 적용</Button>
      </div>
    </RadixDialogContent>
  </RadixDialog>;
}
